import { Fragment, useEffect, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import { IconPlus, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  addAudienceEntry,
  addReleaseApk,
  getAudienceTargets,
  getRelease,
  listStoredApks,
  removeAudienceEntry,
  updateAudienceEntry,
  updateRelease,
} from "@utils/api-communication/HeadsetReleasesCli";
import {
  HARDWARE_FAMILIES,
  type AudienceTarget,
  type AudienceTargets,
  type HardwareFamily,
  type HeadsetRelease,
  type InstallMode,
  type ReleaseSettings,
  type StoredApk,
} from "types/headsetReleaseTypes";
import { AudienceTargetPicker } from "./AudienceTargetPicker";
import { ExpandToggle } from "./ExpandToggle";
import { FamilySlot } from "./FamilySlot";
import { InstallModeControl } from "./InstallModeControl";
import { notifyError, releaseErrorMessage } from "./notifyError";
import { PaceInput } from "./PaceInput";
import { ReleaseHeadsets } from "./ReleaseHeadsets";
import { TargetCell } from "./TargetCell";
import { UpdateProgress } from "./UpdateProgress";
import { canBeLatest, hasEveryApk, minorLineOf, targetName, withFamily } from "./releaseFormat";
import { VERSION_MANAGEMENT_PATH } from "./releaseRoutes";
import { useFamilyUploads } from "./useFamilyUploads";

export default function ReleasePage() {
  const { t } = useTranslation();
  const { version = "" } = useParams();
  const [release, setRelease] = useState<HeadsetRelease>();
  const [targets, setTargets] = useState<AudienceTargets>();
  const [expanded, setExpanded] = useState<string>();
  const [newTarget, setNewTarget] = useState<AudienceTarget>();
  const [newMode, setNewMode] = useState<InstallMode>("available");
  const [storedApks, setStoredApks] = useState<StoredApk[]>([]);

  useEffect(() => {
    Promise.all([getRelease(version), getAudienceTargets()])
      .then(([nextRelease, nextTargets]) => {
        setRelease(nextRelease);
        setTargets(nextTargets);
      })
      .catch(notifyError);
    listStoredApks()
      .then((stored) => setStoredApks(stored.filter((candidate) => candidate.version === version)))
      .catch(notifyError);
  }, [version]);

  const storedApkFor = (family: HardwareFamily) =>
    storedApks.filter((stored) => stored.family === family);

  const run = async (change: () => Promise<unknown>) => {
    try {
      await change();
      setRelease(await getRelease(version));
    } catch (error) {
      notifyError(error);
    }
  };
  const updateSettings = (settings: ReleaseSettings) => run(() => updateRelease(version, settings));

  const addUploadToRelease = async (family: HardwareFamily, uploadId: string) => {
    try {
      setRelease(await addReleaseApk(version, { uploadId, family }));
      markSaved([family]);
      remove(family);
    } catch (error) {
      fail(family, releaseErrorMessage(error));
    }
  };

  const { uploads, start, pickStored, remove, markSaved, fail } = useFamilyUploads(
    (family, uploadId, inspection, fromStorage) => {
      const waitsForStoredChoice = !fromStorage && storedApkFor(family).length > 0;
      if (inspection.versionName !== version) {
        fail(family, t("headsetReleases.versionMismatch", { version }));
      } else if (
        !waitsForStoredChoice &&
        (!inspection.suggestedFamily || inspection.suggestedFamily === family)
      ) {
        addUploadToRelease(family, uploadId);
      }
    },
  );

  if (!release || !targets) {
    return (
      <Box p="lg">
        <Loader />
      </Box>
    );
  }

  const started = release.status === "started";
  const readyToShip = hasEveryApk(release);
  const hasForced = release.audience.some((entry) => entry.mode === "forced");
  const nextStatus = started ? "paused" : "started";

  const selectFamily = (family: HardwareFamily, selected: boolean) => {
    if (!selected) remove(family);
    updateSettings({ hardware: withFamily(release.hardware, family, selected) });
  };

  const addEntry = () =>
    newTarget &&
    run(async () => {
      await addAudienceEntry(version, newTarget, newMode);
      setNewTarget(undefined);
    });

  return (
    <Box p="lg">
      <Stack gap="lg">
        <Anchor component={Link} to={VERSION_MANAGEMENT_PATH} fw={600}>
          ← {t("headsetReleases.releases")}
        </Anchor>

        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Title order={2}>{release.version}</Title>
            <Badge variant="default">{t(`headsetReleases.kind.${release.kind}`)}</Badge>
            <Badge variant="outline" color={started ? "blue" : "gray"}>
              {t(`headsetReleases.releaseStatus.${release.status}`)}
            </Badge>
          </Group>
          <Group gap="lg">
            <Switch
              label={t("headsetReleases.latestForEveryone")}
              checked={release.isLatest}
              disabled={release.isLatest || !canBeLatest(release)}
              onChange={() => updateSettings({ isLatest: true })}
            />
            <Button
              variant={started ? "default" : "filled"}
              disabled={!started && !readyToShip}
              onClick={() => updateSettings({ status: nextStatus })}
            >
              {t(`headsetReleases.action.${nextStatus}`)}
            </Button>
          </Group>
        </Group>

        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Title order={4}>{t("headsetReleases.hardware")}</Title>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              {HARDWARE_FAMILIES.map((family) => (
                <FamilySlot
                  key={family}
                  family={family}
                  selected={release.hardware.includes(family)}
                  onSelectedChange={(selected) => selectFamily(family, selected)}
                  onFile={(file) => start(family, file)}
                  onRemoveUpload={() => remove(family)}
                  onPickStored={(stored) => pickStored(family, stored)}
                  storedApks={storedApkFor(family)}
                  apk={release.apks.find((apk) => apk.family === family)}
                  upload={uploads[family]}
                  expectedVersion={release.version}
                  wrongFamilyAction={
                    <Button
                      size="compact-xs"
                      variant="light"
                      color="orange"
                      onClick={() => {
                        const uploadId = uploads[family]?.uploadId;
                        if (uploadId) addUploadToRelease(family, uploadId);
                      }}
                    >
                      {t("headsetReleases.addAnyway")}
                    </Button>
                  }
                />
              ))}
            </SimpleGrid>
          </Stack>
        </Paper>

        <Paper withBorder radius="md">
          <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("headsetReleases.column.assignedTo")}</Table.Th>
                  <Table.Th w={290}>{t("headsetReleases.column.install")}</Table.Th>
                  <Table.Th w={220}>{t("headsetReleases.column.headsetsUpdated")}</Table.Th>
                  <Table.Th w={50} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {release.audience.map((entry) => {
                  const progress = release.entryProgress?.[entry.id];
                  const isOpen = expanded === entry.id;
                  const name = targetName(entry.target, targets);
                  return (
                    <Fragment key={entry.id}>
                      <Table.Tr>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <ExpandToggle
                              open={isOpen}
                              name={name}
                              onToggle={() => setExpanded(isOpen ? undefined : entry.id)}
                            />
                            <TargetCell target={entry.target} targets={targets} />
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <InstallModeControl
                            label={t("headsetReleases.installModeFor", { name })}
                            value={entry.mode}
                            onChange={(mode) =>
                              run(() => updateAudienceEntry(version, entry.id, mode))
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          {progress && (
                            <UpdateProgress progress={progress.headsets} started={started} />
                          )}
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={t("headsetReleases.removeTarget", { name })}
                            onClick={() => run(() => removeAudienceEntry(version, entry.id))}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                      {isOpen && (
                        <Table.Tr>
                          <Table.Td colSpan={4} pl={48}>
                            <ReleaseHeadsets
                              version={release.version}
                              started={started}
                              entryId={entry.id}
                              defaultGroupBy="org"
                            />
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Group
            p="sm"
            gap="sm"
            style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
          >
            <AudienceTargetPicker
              targets={targets}
              value={newTarget}
              onChange={setNewTarget}
              exclude={release.audience.map((entry) => entry.target)}
            />
            <InstallModeControl
              label={t("headsetReleases.newEntryMode")}
              value={newMode}
              onChange={setNewMode}
            />
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              disabled={!newTarget}
              onClick={addEntry}
            >
              {t("headsetReleases.assign")}
            </Button>
          </Group>
        </Paper>

        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" gap="lg">
            <Group gap="xl">
              <Checkbox
                label={t("headsetReleases.sameMinorOnly", { line: minorLineOf(release.version) })}
                checked={release.sameMinorOnly}
                onChange={(event) => updateSettings({ sameMinorOnly: event.currentTarget.checked })}
              />
              <PaceInput
                key={release.pacePercentPerDay}
                pace={release.pacePercentPerDay}
                onSave={(pace) => updateSettings({ pacePercentPerDay: pace })}
              />
              {hasForced && (
                <DatePickerInput
                  w={180}
                  label={t("headsetReleases.forcedAfter")}
                  clearable
                  value={
                    release.forcedAfter ? dayjs(release.forcedAfter).format("YYYY-MM-DD") : null
                  }
                  onChange={(date) =>
                    updateSettings({
                      forcedAfter: date ? dayjs(date).startOf("day").toISOString() : null,
                    })
                  }
                />
              )}
            </Group>
            {release.progress && (
              <Text fw={600}>
                {t("headsetReleases.orgCount", { count: release.progress.orgs.total })}
                {" · "}
                {t("headsetReleases.headsetCount", { count: release.progress.headsets.total })}
              </Text>
            )}
          </Group>
        </Paper>
      </Stack>
    </Box>
  );
}
